import { Tooltip } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import DeleteOutlineOutlined from '@material-ui/icons/DeleteOutlineOutlined';
import Alert from '@material-ui/lab/Alert';
import { Formik, Form as FormikForm, Field, FieldArray } from 'formik';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CharacterCounterFormField } from 'v2/components/Shared/FormFields/CharacterCounterFormField';
import CurrencyFormField from 'v2/components/Shared/FormFields/CurrencyFormField';
import TextFormField from 'v2/components/Shared/FormFields/TextFormField';
import { OfferCurrencyAlert } from 'v2/components/Shared/OfferCurrencyAlert';
import { Space } from 'v2/components/Shared/Space';
import { getCurrencySymbol } from 'v2/helpers/getCurrencySymbol';
import { useCreateProductOffer } from 'v2/hooks/useCreateProductOffer';
import * as yup from 'yup';

import type { Currency } from 'v2/interfaces/currency';

import { useStyles } from './styles';
import { ChooseCurrenciesModal } from '../ChooseCurrenciesModal';

import type { FormikHelpers } from 'formik';

const MIN_CURRENCY_VALUE = 5;

const validationSchema = yup.object().shape({
  name: yup.string().required('Nome da oferta é obrigatório').min(6).max(100),
  description: yup.string().optional().min(100).max(4000),
  prices: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.number().required().min(MIN_CURRENCY_VALUE, 'O valor deve ser no mínimo 5'),
        currency_code: yup.string().required(),
      })
    )
    .required(),
});

type FormValues = {
  name: string;
  description: string;
  prices: Array<{
    value: number;
    currency_code: string;
  }>;
};

type FormProps = {
  productId: number;
};

function Form({ productId }: FormProps) {
  const classes = useStyles();
  const history = useHistory();
  const [isChooseCurrenciesModalOpen, setIsChooseCurrenciesModalOpen] = React.useState(false);
  const [selectedCurrencies, setSelectedCurrencies] = React.useState<Currency[]>([]);
  const createOfferMutation = useCreateProductOffer({
    onSuccess: () => {
      toast.success('Oferta criada com sucesso');
      history.push('/producer/product/editproduct/' + productId + '/sales');
    },
    onError: () => {
      toast.error('Erro ao criar oferta');
    },
  });

  function addCurrencies(currencies: Currency[]) {
    setSelectedCurrencies([...selectedCurrencies, ...currencies]);
  }

  function removeCurrency(currencyId: number) {
    setSelectedCurrencies(selectedCurrencies.filter((selectedCurrency) => selectedCurrency.id !== currencyId));
  }

  function handleCancel() {
    history.push('/producer/product/editproduct/' + productId + '/sales');
  }

  const onSubmit = React.useCallback(
    async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
      if (values.description === '') {
        delete values.description;
      }
      await createOfferMutation.mutateAsync({
        productId,
        ...values,
      });
      setSubmitting(false);
    },
    [createOfferMutation, productId]
  );

  return (
    <Formik
      initialValues={{
        name: '',
        description: '',
        prices: [{ value: 0, currency_code: 'BRL' }],
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting }) => (
        <FormikForm className={classes.container}>
          <Field id='offer-name' name='name' component={TextFormField} label='Nome da oferta' maxLength={255} />
          <Field
            id='offer-description'
            name='description'
            component={CharacterCounterFormField}
            label='Descrição da oferta para checkout (opcional)'
            limits={4000}
            minimum={100}
          />
          <FieldArray name='prices'>
            {({ push, remove }) => (
              <>
                <Paper className={classes.card} elevation={0}>
                  <TableContainer>
                    <Table size='small'>
                      <TableHead>
                        <TableCell width={200}>Moeda</TableCell>
                        <TableCell width={200}>Valor</TableCell>
                        <TableCell>&nbsp;</TableCell>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell className={classes.currencyCell}>BRL (Real Brasileiro)</TableCell>
                          <TableCell>
                            <CurrencyFormField
                              id='offer-price-BRL'
                              name='prices.0.value'
                              InputProps={{
                                startAdornment: <InputAdornment position='start'>R$</InputAdornment>,
                              }}
                              inputProps={{
                                maxLength: 20,
                                style: {
                                  width: 100,
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Space size={6} axis='vertical' />
                  <Alert severity='info'>É obrigatória a configuração do valor em real para a conversão automática de suas vendas.</Alert>
                  <Space size={32} axis='vertical' />
                  <div className={classes.cardHeader}>
                    <Typography>Cadastre valores diferenciados para moedas internacionais</Typography>
                    <Button id='add-currency-button' variant='outlined' onClick={() => setIsChooseCurrenciesModalOpen(true)}>
                      Cadastrar novo valor
                    </Button>
                  </div>
                  <Space size={16} axis='vertical' />
                  <TableContainer>
                    <Table size='small'>
                      <TableHead>
                        <TableCell width={200}>Moeda</TableCell>
                        <TableCell width={200}>Valor</TableCell>
                        <TableCell align='right' style={{ paddingRight: 20 }}>
                          Ações
                        </TableCell>
                      </TableHead>
                      <TableBody>
                        {selectedCurrencies.length === 0 && (
                          <TableRow>
                            <TableCell
                              align='center'
                              colSpan={3}
                              style={{
                                padding: 32,
                              }}
                            >
                              Nenhuma moeda cadastrada
                            </TableCell>
                          </TableRow>
                        )}
                        {selectedCurrencies.map((row, index) => (
                          <TableRow key={row.id}>
                            <TableCell className={classes.currencyCell}>
                              {row.code} ({row.name})
                            </TableCell>
                            <TableCell>
                              <CurrencyFormField
                                id={`offer-price-${row.code}`}
                                name={`prices.${index + 1}.value`}
                                currency={row.code}
                                inputProps={{
                                  maxLength: 20,
                                  style: {
                                    width: 100,
                                  },
                                }}
                                InputProps={{
                                  startAdornment: <InputAdornment position='start'>{getCurrencySymbol('pt-BR', row.code)}</InputAdornment>,
                                }}
                              />
                            </TableCell>
                            <TableCell align='right'>
                              <Tooltip title='Remover' placement='top' arrow>
                                <IconButton
                                  id={`remove-currency-button-${row.code}`}
                                  onClick={() => {
                                    remove(index + 1);
                                    removeCurrency(row.id);
                                  }}
                                >
                                  <DeleteOutlineOutlined />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Space size={16} axis='vertical' />
                  <OfferCurrencyAlert />
                </Paper>
                <footer className={classes.footer}>
                  <Button id='cancel-offer-button' variant='outlined' onClick={handleCancel}>
                    Cancelar
                  </Button>
                  <Button
                    id='create-offer-button'
                    variant='contained'
                    color='primary'
                    type='submit'
                    disabled={isSubmitting}
                    startIcon={isSubmitting && <CircularProgress size={20} color='inherit' />}
                  >
                    Cadastrar oferta
                  </Button>
                </footer>
                <ChooseCurrenciesModal
                  isOpen={isChooseCurrenciesModalOpen}
                  closeModal={() => setIsChooseCurrenciesModalOpen(false)}
                  selectedCurrencies={selectedCurrencies}
                  addCurrencies={(currencies) => {
                    currencies.forEach((currency) =>
                      push({
                        value: 0,
                        currency_code: currency.code,
                      })
                    );
                    addCurrencies(currencies);
                  }}
                />
              </>
            )}
          </FieldArray>
        </FormikForm>
      )}
    </Formik>
  );
}

export { Form };
